import { useContext, useEffect } from 'react'

import { useFormContext } from 'react-hook-form'

import { PaddingInset } from '@cash-design-system/react'
import { SubmitFormRequestElementResult } from '@cash-web/protos/squareup/franklin/app/submit_form.pb'
import { FormBlockerElementCellDefaultElement } from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import { ArcadeCellFromProto } from '@cash-web/shared/ui-arcade-cell'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { getBlockerAction } from '@cash-web/shared-flow-feature-main'
import { WebViewUnsupportedFeature } from '@squareup/customer-data-events-web'

import { FormBlockerContext } from '../../FormBlockerContext'

export const getCellDefaultCheckedElementKey = (formSectionIndex: number) =>
  `${formSectionIndex}.cell_default_result.is_checked`

export const CellDefaultElement = ({
  cell,
  cell_action: cellAction,
  selection_group_id: selectionGroupId,
  initial_selection: initialSelection,
  formSectionIndex,
}: FormBlockerElementCellDefaultElement & { formSectionIndex: number }) => {
  const logCdfEvent = useLogCashWebCdfEvent()
  const context = useContext(FormBlockerContext)
  const { register, setValue, getValues } = useFormContext()

  const cellDefaultCheckedElementKey = getCellDefaultCheckedElementKey(formSectionIndex)
  const hasRadioAccessory = cell?.accessory?.radio
  const hasAccessoryWithInput = hasRadioAccessory || cell?.accessory?.checkbox || cell?.accessory?.toggle

  useEffect(() => {
    if (cellAction) {
      const blockerAction = getBlockerAction(cellAction)
      if (!blockerAction.isSupported) {
        logCdfEvent(
          WebViewUnsupportedFeature({
            source: 'CellDefaultElement',
            feature: 'BLOCKER_ACTION',
            action: blockerAction.actionName,
          })
        )
      }
    }
  }, [cellAction, logCdfEvent])

  const getAccessoryInputProps = () => {
    if (!hasAccessoryWithInput) return undefined

    /**
     * If the cell has a radio accessory, we need to handle the change event for the radio button.
     * We cannot register the radio button input in the form because then we won't be sending the data in the correct format.
     *
     * Instead, we need to handle the change event manually and update the form value ourselves.
     */
    if (hasRadioAccessory) {
      return {
        name: selectionGroupId,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          setValue(cellDefaultCheckedElementKey, e.target.checked)
          const formValues = getValues()
          // Mark all other radio buttons in the same selection group as unchecked
          Object.entries(formValues).forEach(
            ([formSectionIndexKey, section]: [string, SubmitFormRequestElementResult]) => {
              const formSectionIndexNumber = Number(formSectionIndexKey)
              if (formSectionIndex === formSectionIndexNumber) return
              const groupId = section?.cell_default_result?.selection_group_id
              if (groupId === selectionGroupId) {
                setValue(getCellDefaultCheckedElementKey(formSectionIndexNumber), false)
              }
            }
          )
        },
      }
    }
    return register(cellDefaultCheckedElementKey, { value: initialSelection ?? false })
  }

  if (!cell) {
    return null
  }

  return (
    <PaddingInset>
      {selectionGroupId && (
        <input
          type="hidden"
          value={selectionGroupId}
          {...register(`${formSectionIndex}.cell_default_result.selection_group_id`)}
        />
      )}

      {hasRadioAccessory && (
        <input type="hidden" {...register(cellDefaultCheckedElementKey, { value: initialSelection ?? false })} />
      )}

      <ArcadeCellFromProto
        protoProps={cell}
        onClick={() => {
          if (cellAction) {
            context.onFormBlockerAction(cellAction)
          }
        }}
        selectionGroupId={selectionGroupId}
        initialSelection={initialSelection ?? false}
        accessoryInputProps={getAccessoryInputProps()}
      />
    </PaddingInset>
  )
}
